// ─── Cuan · supabase.js ───────────────────────────────────────────────────────
// Raw-fetch Supabase client + Supabase Auth helpers.
// No SDK dependency — keeps the bundle lean.

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

// ── Session storage ────────────────────────────────────────────────────────────

const SESSION_KEY = 'cuan_session';

export function getStoredSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY)); }
  catch { return null; }
}

export function storeSession(session) {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else localStorage.removeItem(SESSION_KEY);
}

// Returns the best available token for API calls.
// After login this is the user's JWT; before login it falls back to the anon key
// (so unauthenticated boot still works — remove this fallback once RLS is locked down).
function getToken() {
  return getStoredSession()?.access_token ?? SUPABASE_KEY;
}

// ── REST helpers ───────────────────────────────────────────────────────────────

async function sb(table, opts = {}) {
  const { method = 'GET', body, params = '' } = opts;
  const url = `${SUPABASE_URL}/rest/v1/${table}${params}`;
  const res = await fetch(url, {
    method,
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      Prefer: method === 'POST' || method === 'PATCH' ? 'return=representation' : '',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  if (res.status === 204) return null;
  return res.json();
}

export const dbGet    = (table, params = '')      => sb(table, { params });
export const dbPatch  = (table, id, body)         => sb(table, { method: 'PATCH',  body, params: `?id=eq.${id}` });
export const dbPost   = (table, body)             => sb(table, { method: 'POST',   body });
export const dbDelete = (table, id)               => sb(table, { method: 'DELETE', params: `?id=eq.${id}` });

// ── Auth helpers ───────────────────────────────────────────────────────────────

/** Sign in with email + password. Stores session; throws on failure. */
export async function authSignIn(email, password) {
  const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
    method: 'POST',
    headers: { apikey: SUPABASE_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error_description || err.msg || 'Sign-in failed. Check your email and password.');
  }
  const session = await res.json();
  storeSession(session);
  return session;
}

/** Sign out — invalidates the token server-side and clears local storage. */
export async function authSignOut() {
  const session = getStoredSession();
  if (session?.access_token) {
    // Best-effort — ignore network errors on sign-out
    await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${session.access_token}`,
      },
    }).catch(() => {});
  }
  storeSession(null);
}

/** Returns the current user object from Supabase, or null if the session is invalid. */
export async function authGetUser() {
  const session = getStoredSession();
  if (!session?.access_token) return null;
  const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${session.access_token}`,
    },
  });
  if (!res.ok) { storeSession(null); return null; }
  return res.json();
}
